import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { trackButtonClick } from "@/utils/analytics";
import { readSeenCount, writeSeenCount } from "./quizMenuSeen";
import { useQuizLinks } from "./useQuizLinks";

const base = "sections.rateQuiz";

/**
 * Small icon button beside the header CTA that opens every quiz in a menu.
 * Its counter is red until the visitor opens the menu once, then neutral; it
 * turns red again when a new quiz goes live. The counter renders neutral on
 * the server and on first paint, so returning visitors never see it flash.
 */
export const QuizHeaderMenu = () => {
  const { t } = useTranslation();
  const { lang, hub, items } = useQuizLinks();
  const [isNew, setIsNew] = useState(false);
  const count = items.length;

  useEffect(() => {
    setIsNew(readSeenCount() < count);
  }, [count]);

  if (count === 0) return null;

  const onOpenChange = (open: boolean) => {
    if (!open) return;
    trackButtonClick("quiz_header_menu_open", isNew ? "new" : "seen");
    writeSeenCount(count);
    setIsNew(false);
  };

  return (
    <DropdownMenu modal={false} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${t(`${base}.ticker.lead`)} (${count})`}
          title={t(`${base}.ticker.lead`)}
          className="relative grid h-10 w-10 place-items-center rounded-md border border-border bg-background text-primary transition-colors hover:border-primary/30 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:border-primary/30 data-[state=open]:bg-primary/5"
          data-testid="button-quiz-menu"
        >
          <ClipboardCheck className="h-5 w-5" aria-hidden="true" />
          <span
            className={cn(
              "absolute -right-1 -top-1 grid h-3.5 min-w-[14px] place-items-center rounded-full border-[1.5px] border-background px-[3px] text-[9px] font-bold leading-none transition-colors",
              isNew ? "bg-red-600 text-white" : "bg-muted text-muted-foreground",
            )}
            aria-hidden="true"
            data-testid="quiz-menu-count"
            data-new={isNew || undefined}
          >
            {count}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-[min(92vw,22rem)] p-2" data-testid="menu-quizzes">
        <DropdownMenuLabel className="flex items-center gap-2 text-sm">
          <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-white">
            {t(`${base}.ticker.badge`)}
          </span>
          {t(`${base}.ticker.lead`)}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map(({ key, icon: Icon, path, label }) => (
          <DropdownMenuItem key={key} asChild className="gap-3 py-2">
            <Link
              to={path}
              onClick={() => trackButtonClick("quiz_header_menu_open_quiz", key)}
              data-testid={`menu-quiz-${key}`}
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="leading-snug">{label}</span>
            </Link>
          </DropdownMenuItem>
        ))}
        {hub && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="justify-center gap-1.5 py-2.5 font-semibold text-primary">
              <Link
                to={hub}
                onClick={() => trackButtonClick("quiz_header_menu_all", lang)}
                data-testid="menu-quiz-all"
              >
                {t(`${base}.bubble.all`)}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
